<template>
  <div class="spinning-wheel-container">
    <div class="wheel-container">
      <!-- SVG-based wheel for better control and precision -->
      <svg
        class="wheel"
        ref="wheel"
        :style="wheelStyle"
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
      >
        <!-- Outer circle/border -->
        <circle cx="50" cy="50" r="50" fill="white" />

        <!-- Wheel segments -->
        <g v-for="(segment, index) in segments" :key="index">
          <!-- Pie segment -->
          <path
            :d="getSegmentPath(index)"
            :fill="getSegmentColor(index)"
            stroke="white"
            stroke-width="0.5"
          />

          <!-- Text path for curved text -->
          <path
            :id="`textPath-${index}`"
            :d="getTextPath(index)"
            fill="none"
          />

          <!-- Text element -->
          <text
            :fill="getTextColor(segment.color)"
            :font-size="getFontSize(segment.label)"
            font-weight="bold"
            text-anchor="middle"
            class="segment-text"
            alignment-baseline="middle"
          >
            <textPath
              :href="`#textPath-${index}`"
              startOffset="50%"
              class="segment-text-path"
              lengthAdjust="spacingAndGlyphs"
              textLength="25"
            >
              {{ getFormattedLabel(segment.label) }}
            </textPath>
          </text>
        </g>

        <!-- Center circle -->
        <circle cx="50" cy="50" r="8" fill="white" stroke="#e0e0e0" stroke-width="1" />
        <circle cx="50" cy="50" r="2" fill="#333" />
      </svg>

      <!-- Marker/pointer -->
      <div class="wheel-marker">
        <svg viewBox="0 0 30 30" width="100%" height="100%">
          <polygon points="15,0 0,30 30,30" fill="#4CAF50" />
          <!-- Add a small circle at the bottom for better appearance -->
          <!-- <circle cx="15" cy="25" r="3" fill="white" /> -->
        </svg>
      </div>
    </div>

    <div class="wheel-controls">
      <v-btn
        color="green"
        large
        dark
        :loading="isSpinning"
        @click="spinWheel"
        class="spin-button"
      >
        {{ isSpinning ? 'Spinning...' : 'SPIN' }}
      </v-btn>
    </div>

    <!-- Result Dialog -->
    <v-dialog v-model="showResult" max-width="400">
      <v-card>
        <v-card-title class="headline">Congratulations!</v-card-title>
        <v-card-text>
          <div class="result-content">
            <h2>You won:</h2>
            <div class="prize-display">
              {{ currentPrize ? currentPrize.label : '' }}
            </div>
            <p v-if="currentPrize && currentPrize.description">{{ currentPrize.description }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeResult">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'SpinningWheel',
  props: {
    segments: {
      type: Array,
      required: true,
      default: () => []
    },
    // Optional custom colors for segments
    colors: {
      type: Array,
      default: () => ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8AC24A', '#FF5252']
    }
  },
  data() {
    return {
      isSpinning: false,
      rotation: 0,
      showResult: false,
      currentPrize: null,
      spinDuration: 5000, // 5 seconds
      spinRevolutions: 5, // Number of full rotations
      defaultColors: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8AC24A', '#FF5252']
    };
  },
  computed: {
    wheelStyle() {
      // Use a custom cubic-bezier curve for more realistic spinning with a slight wobble at the end
      // Always use the same transition timing for consistent spin speed
      const transitionTiming = this.isSpinning ?
        `transform ${this.spinDuration / 1000}s cubic-bezier(0.17, 0.67, 0.83, 0.97)` :
        'transform 0s';

      return {
        transform: `rotate(${this.rotation}deg)`,
        transition: transitionTiming,
        willChange: 'transform' // Optimize for animations
      };
    },
    segmentAngle() {
      return 360 / this.segments.length;
    }
  },
  methods: {
    // Generate SVG path for a pie segment
    getSegmentPath(index) {
      const angle = this.segmentAngle;
      // Start at top (90 degrees offset) and add a small offset to align with the marker
      const startAngle = index * angle - 90;
      const endAngle = startAngle + angle;

      // Convert angles to radians
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      // Calculate points on the circle
      const x1 = 50 + 48 * Math.cos(startRad);
      const y1 = 50 + 48 * Math.sin(startRad);
      const x2 = 50 + 48 * Math.cos(endRad);
      const y2 = 50 + 48 * Math.sin(endRad);

      // Create SVG path - move to center, line to first point, arc to second point, close path
      const largeArcFlag = angle > 180 ? 1 : 0;
      return `M 50 50 L ${x1} ${y1} A 48 48 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    },

    // Generate path for text placement
    getTextPath(index) {
      const angle = this.segmentAngle;
      const midAngle = (index * angle + angle / 2 - 90) * (Math.PI / 180);

      // Calculate radius for text placement (further inward from edge for more space)
      const radius = 30; // Reduced from 35 to move text inward

      // Calculate the middle angle of the segment for arc placement

      // Create a larger arc for text placement
      const arcLength = 30; // Increased from 20 to give more space for text
      const startAngle = midAngle - arcLength / radius / 2;
      const endAngle = midAngle + arcLength / radius / 2;

      const x1 = 50 + radius * Math.cos(startAngle);
      const y1 = 50 + radius * Math.sin(startAngle);
      const x2 = 50 + radius * Math.cos(endAngle);
      const y2 = 50 + radius * Math.sin(endAngle);

      return `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`;
    },

    // Get segment color
    getSegmentColor(index) {
      const colorIndex = index % this.colors.length;
      return this.colors[colorIndex] || this.defaultColors[colorIndex % this.defaultColors.length];
    },

    // Format label text to fit in the segment
    getFormattedLabel(label) {
      if (!label) return '';

      // Get the number of segments to determine how much space is available
      const segmentCount = this.segments.length;

      // Calculate max length based on segment count
      // More segments = less space per segment
      let maxLength = 14; // Increased from 12 to allow more text

      if (segmentCount > 8) {
        maxLength = 10; // Increased from 8
      } else if (segmentCount > 6) {
        maxLength = 12; // Increased from 10
      }

      // For mobile devices, use shorter text
      if (window.innerWidth < 480) {
        maxLength = Math.max(6, maxLength - 4); // Increased from 4 to 6
      }

      // Special handling for common patterns
      if (label.includes('%') && label.includes('Discount')) {
        // For percentage discount labels, ensure the percentage is visible
        const percentMatch = label.match(/(\d+)%/);
        if (percentMatch) {
          const percent = percentMatch[1];
          return `${percent}% OFF`; // Standardize to shorter form
        }
      } else if (label.includes('$') && label.includes('discount')) {
        // For fixed amount discount labels
        const amountMatch = label.match(/\$(\d+)/);
        if (amountMatch) {
          const amount = amountMatch[1];
          return `$${amount} OFF`; // Standardize to shorter form
        }
      }

      // Truncate and add ellipsis if needed
      if (label.length > maxLength) {
        // Try to find a space to break at
        const spaceIndex = label.lastIndexOf(' ', maxLength - 3);
        if (spaceIndex > maxLength / 2) {
          return label.substring(0, spaceIndex) + '...';
        }
        return label.substring(0, maxLength - 3) + '...';
      }

      return label;
    },

    // Get appropriate font size based on label length and segment count
    getFontSize(label) {
      if (!label) return 4;

      // Base font size on segment count and label length
      const segmentCount = this.segments.length;
      const labelLength = label.length;

      // Start with default size
      let fontSize = 4;

      // Adjust based on segment count
      if (segmentCount > 8) {
        fontSize = 3.5;
      } else if (segmentCount > 6) {
        fontSize = 3.8;
      }

      // Further adjust based on label length
      if (labelLength > 10) {
        fontSize -= 0.5;
      } else if (labelLength > 7) {
        fontSize -= 0.3;
      }

      // Ensure minimum size
      return Math.max(2.8, fontSize);
    },

    // Determine text color based on background color brightness
    getTextColor(backgroundColor) {
      // Default to white if no color is provided
      if (!backgroundColor) return 'white';

      // Extract RGB components
      let r, g, b;
      if (backgroundColor.startsWith('#')) {
        const hex = backgroundColor.substring(1);
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else if (backgroundColor.startsWith('rgb')) {
        const rgbMatch = backgroundColor.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
          [r, g, b] = rgbMatch.map(Number);
        } else {
          return 'white';
        }
      } else {
        return 'white';
      }

      // Calculate brightness using the YIQ formula
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // Return white for dark backgrounds, black for light backgrounds
      return brightness > 128 ? 'black' : 'white';
    },
    spinWheel() {
      if (this.isSpinning) return;

      // Directly perform the spin without resetting rotation
      // This ensures the wheel always spins in the same direction
      this.performSpin();
    },

    performSpin() {
      this.isSpinning = true;

      // Calculate the winning segment based on probabilities
      const winningSegment = this.getWinningSegment();
      this.currentPrize = this.segments[winningSegment];

      // Calculate the final rotation
      const segmentAngle = 360 / this.segments.length;

      // Calculate base rotation (multiple full rotations)
      const baseRotation = this.spinRevolutions * 360;

      // Calculate the winning position
      // Add half a segment angle to land in the middle of the segment
      const winningRotation = (360 - (winningSegment * segmentAngle) - (segmentAngle / 2)) % 360;

      // Note: We don't need to normalize the current rotation as we're always adding to it

      // Calculate how much we need to rotate to reach the target position
      // We always want to rotate clockwise (add more rotation)
      const targetRotation = baseRotation + winningRotation;

      // Set the final rotation (current rotation + additional rotation needed)
      // This ensures we always spin in the same direction (clockwise)
      const finalRotation = this.rotation + targetRotation;
      this.rotation = finalRotation;

      // Add a subtle wobble effect at the end of the spin
      const wobbleTime = 500; // 500ms for the wobble effect

      // Show result after spin completes (including wobble time)
      setTimeout(() => {
        this.isSpinning = false;

        // Small delay before showing the result for better UX
        setTimeout(() => {
          this.showResult = true;
          // Emit event with the prize
          this.$emit('prize-won', this.currentPrize);
        }, 300);
      }, this.spinDuration + wobbleTime);
    },
    getWinningSegment() {
      // Check if segments have probabilities
      const hasProbabilities = this.segments.some(segment => segment.probability !== undefined);

      if (hasProbabilities) {
        // Use weighted random selection based on probabilities
        const random = Math.random();
        let cumulativeProbability = 0;

        for (let i = 0; i < this.segments.length; i++) {
          // Use the segment's probability or equal distribution if not specified
          const probability = this.segments[i].probability !== undefined
            ? this.segments[i].probability
            : 1 / this.segments.length;

          cumulativeProbability += probability;

          if (random <= cumulativeProbability) {
            return i;
          }
        }

        // Fallback to random selection if probabilities don't add up to 1
        return Math.floor(Math.random() * this.segments.length);
      } else {
        // Simple random selection if no probabilities are defined
        return Math.floor(Math.random() * this.segments.length);
      }
    },
    closeResult() {
      this.showResult = false;
    }
  }
};
</script>

<style scoped>
.spinning-wheel-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 100%;
}

.segment-text {
  font-family: Arial, sans-serif;
  font-weight: bold;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.1);
  stroke-width: 0.5px;
}

.segment-text-path {
  letter-spacing: 0.2px;
  dominant-baseline: middle;
  text-rendering: optimizeLegibility;
}

.wheel-container {
  position: relative;
  width: 400px;
  height: 400px;
  margin: 0 auto 30px;
  max-width: 100%;
  filter: drop-shadow(0px 5px 15px rgba(0, 0, 0, 0.1));
  padding-top: 0px; /* Removed extra top padding */
}

.wheel {
  position: relative;
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 10px rgba(0, 0, 0, 0.15));
  will-change: transform;
}

text {
  font-family: Arial, sans-serif;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.3);
  dominant-baseline: middle;
}

.wheel-marker {
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  width: 30px;
  height: 30px;
  z-index: 2;
  filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.3));
  pointer-events: none;
}

.wheel-controls {
  margin-top: 30px;
  text-align: center;
}

.spin-button {
  min-width: 180px;
  font-size: 20px;
  font-weight: bold;
  padding: 12px 24px !important;
  border-radius: 30px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  letter-spacing: 1px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.spin-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
}

.spin-button:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

@media (max-width: 480px) {
  .wheel-container {
    width: 300px;
    height: 300px;
  }

  .wheel-marker {
    width: 25px;
    height: 25px;
    top: -16px; /* Adjusted for mobile view */
  }

  .segment-text {
    font-size: 3.2;
    stroke-width: 0.3px;
  }

  .segment-text-path {
    letter-spacing: 0;
  }

  .spin-button {
    min-width: 150px;
    font-size: 18px;
    padding: 10px 20px !important;
  }
}

.result-content {
  text-align: center;
  padding: 20px 0;
}

.prize-display {
  font-size: 24px;
  font-weight: bold;
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #4CAF50;
}
</style>
